/**
 * Proximity Context
 * =================
 * Manages proximity discovery state and location updates.
 * 
 * PRIVACY PRINCIPLES:
 * - User must explicitly opt-in to proximity
 * - Location is only collected when proximity is enabled
 * - Exact location is never shown to other users
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Update location every 30 seconds when proximity is active
const LOCATION_UPDATE_INTERVAL = 30000;

// Check for nearby members every 10 seconds
const NEARBY_CHECK_INTERVAL = 10000;

const ProximityContext = createContext(null);

export const useProximity = () => {
  const context = useContext(ProximityContext);
  if (!context) {
    throw new Error('useProximity must be used within a ProximityProvider');
  }
  return context;
};

export const ProximityProvider = ({ children }) => {
  // Proximity state
  const [proximityEnabled, setProximityEnabled] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [visibilityMode, setVisibilityMode] = useState('active');
  const [permissionStatus, setPermissionStatus] = useState('not_requested');
  
  // Location state
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Nearby members
  const [nearbyMembers, setNearbyMembers] = useState([]);
  const [nearbyCount, setNearbyCount] = useState(0);
  const [loadingNearby, setLoadingNearby] = useState(false);
  
  // Refs for intervals
  const locationWatchId = useRef(null);
  const nearbyCheckInterval = useRef(null);

  // Fetch proximity status on mount
  useEffect(() => {
    fetchProximityStatus();
  }, []);

  // Start/stop location tracking based on proximity state
  useEffect(() => {
    if (proximityEnabled && permissionStatus === 'granted') {
      startLocationTracking();
      startNearbyChecks();
    } else {
      stopLocationTracking();
      stopNearbyChecks();
    }

    return () => {
      stopLocationTracking();
      stopNearbyChecks();
    };
  }, [proximityEnabled, permissionStatus]);

  const fetchProximityStatus = async () => {
    try {
      const res = await fetch(`${API}/proximity/status`);
      if (res.ok) {
        const data = await res.json();
        setProximityEnabled(data.enabled);
        setConsentGiven(data.consent_given);
        setVisibilityMode(data.visibility_mode);
        setPermissionStatus(data.permission_status);
      }
    } catch (error) {
      console.error('Failed to fetch proximity status:', error);
    }
  };

  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    // Clear any existing watch
    if (locationWatchId.current) {
      navigator.geolocation.clearWatch(locationWatchId.current);
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000
    };

    // Watch position for continuous updates
    locationWatchId.current = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocationAccuracy(accuracy);
        setLastLocationUpdate(new Date().toISOString());
        setLocationError(null);

        // Send location update to server
        try {
          await fetch(`${API}/proximity/location`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              latitude,
              longitude,
              accuracy_meters: accuracy
            })
          });
        } catch (error) {
          console.error('Failed to update location:', error);
        }
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setPermissionStatus('denied');
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('Unknown location error');
        }
      },
      options
    );
  }, []);

  const stopLocationTracking = useCallback(() => {
    if (locationWatchId.current) {
      navigator.geolocation.clearWatch(locationWatchId.current);
      locationWatchId.current = null;
    }
  }, []);

  const startNearbyChecks = useCallback(() => {
    // Initial fetch
    fetchNearbyMembers();

    // Set up interval
    nearbyCheckInterval.current = setInterval(() => {
      fetchNearbyMembers();
    }, NEARBY_CHECK_INTERVAL);
  }, []);

  const stopNearbyChecks = useCallback(() => {
    if (nearbyCheckInterval.current) {
      clearInterval(nearbyCheckInterval.current);
      nearbyCheckInterval.current = null;
    }
    setNearbyMembers([]);
    setNearbyCount(0);
  }, []);

  const fetchNearbyMembers = async () => {
    if (!proximityEnabled) return;

    setLoadingNearby(true);
    try {
      const res = await fetch(`${API}/proximity/nearby`);
      if (res.ok) {
        const data = await res.json();
        setNearbyMembers(data.nearby_members || []);
        setNearbyCount(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch nearby members:', error);
    } finally {
      setLoadingNearby(false);
    }
  };

  const enableProximity = async () => {
    // First request browser permission
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported');
      return { success: false, error: 'Geolocation not supported' };
    }

    try {
      // Request permission by trying to get position
      await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });

      // Permission granted, now enable on server
      const res = await fetch(`${API}/proximity/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_acknowledged: true,
          consent_version: '1.0'
        })
      });

      if (res.ok) {
        setProximityEnabled(true);
        setConsentGiven(true);
        setPermissionStatus('granted');
        setVisibilityMode('active');
        return { success: true };
      } else {
        const error = await res.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      if (error.code === 1) { // Permission denied
        setPermissionStatus('denied');
        return { success: false, error: 'Location permission denied' };
      }
      return { success: false, error: error.message };
    }
  };

  const disableProximity = async () => {
    try {
      const res = await fetch(`${API}/proximity/disable`, {
        method: 'POST'
      });

      if (res.ok) {
        setProximityEnabled(false);
        setVisibilityMode('active');
        stopLocationTracking();
        stopNearbyChecks();
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to disable proximity:', error);
    }
    return { success: false };
  };

  const pauseVisibility = async (durationMinutes = null) => {
    try {
      const url = durationMinutes 
        ? `${API}/proximity/pause?duration_minutes=${durationMinutes}`
        : `${API}/proximity/pause`;
      
      const res = await fetch(url, { method: 'POST' });
      
      if (res.ok) {
        setVisibilityMode('paused');
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to pause visibility:', error);
    }
    return { success: false };
  };

  const resumeVisibility = async () => {
    try {
      const res = await fetch(`${API}/proximity/resume`, { method: 'POST' });
      
      if (res.ok) {
        setVisibilityMode('active');
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to resume visibility:', error);
    }
    return { success: false };
  };

  const value = {
    // State
    proximityEnabled,
    consentGiven,
    visibilityMode,
    permissionStatus,
    locationAccuracy,
    lastLocationUpdate,
    locationError,
    nearbyMembers,
    nearbyCount,
    loadingNearby,
    
    // Actions
    enableProximity,
    disableProximity,
    pauseVisibility,
    resumeVisibility,
    fetchNearbyMembers,
    fetchProximityStatus
  };

  return (
    <ProximityContext.Provider value={value}>
      {children}
    </ProximityContext.Provider>
  );
};

export default ProximityContext;
